// Basic
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsOrganizations from '../../../api/actionsOrganizations';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import Switch from '@/common-utils/components/ui/switch';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import ScrollArea from '@/common-utils/components/ui/scrollArea';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Accordion, AccordionContent, AccordionTrigger, AccordionItem } from '@/common-utils/components/ui/accordion';
// Common
import SIM_OFFERS_PROVIDERS from '../../../../sims/enums/SimOffersProviders';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.organizationSimOffers
 * @param root0.isLoading
 * @param root0.organizationId
 * @param root0.setLaunchRefetch
 * @returns {JSX}
 */
function ModalUpdateOrganizationSimOffers({ isOpen, setIsOpen, organizationId, organizationSimOffers, isLoading, setLaunchRefetch }) {
    // Toast
    const { toast } = useToast();

    // Functions
    const getSimOffersCategories = () => {
        const categories = {};
        Object.keys(organizationSimOffers).forEach((key) => {
            const foundCategory = Object.values(SIM_OFFERS_PROVIDERS).find(category => category.name === key);
            if (foundCategory) {
                categories[foundCategory.key] = SIM_OFFERS_PROVIDERS[foundCategory.key];
            }
        });
        return categories;
    };

    // Hooks - useMemo
    // Memoize constants based on props
    const { hasData, categories } = useMemo(() => {
        return {
            hasData: organizationSimOffers ? !!Object.keys(organizationSimOffers).length : false,
            categories: organizationSimOffers ? Object.values(getSimOffersCategories()) : []
        };
    }, [organizationSimOffers]);
    const allCategoryNames = useMemo(() => categories.map(cat => cat.name), [categories]);

    // State
    const [showAllOffers, setShowAllOffers] = useState(false);
    const [simOffersCopy, setSimOffersCopy] = useState(organizationSimOffers || {});
    const [accordionsOpened, setAccordionsOpened] = useState([]);

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity='modal.update_sim_offers.success' namespace={nsOrganization} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.update_sim_offers.error' namespace={nsOrganization} />,
            variant: 'danger'
        });
    };
    const updateSimOffers = actionsOrganizations.updateSimOffers(onSuccess, onError);

    // Hooks
    // Update sim offers copy when data changes from props
    useEffect(() => {
        setSimOffersCopy(organizationSimOffers);
    }, [organizationSimOffers]);
    // Update accordions opened when all category names change
    useEffect(() => {
        if (allCategoryNames.length > 0) {
            setAccordionsOpened(allCategoryNames);
            setShowAllOffers(true);
        }
    }, [allCategoryNames]);

    // Functions
    // Handle accordion change
    const handleAccordionChange = (values) => {
        setAccordionsOpened(values);
        setShowAllOffers(values.length !== 0);
    };

    // Handle show all switch change
    const handleSwitchChange = (checked) => {
        setShowAllOffers(checked);
        setAccordionsOpened(checked ? allCategoryNames : []);
    };
    // Handle offer change
    const handleOfferChange = (value, category, index) => {
        const newOffersState = {
            [category]: simOffersCopy[category].map((offer, i) => i === index ? { ...offer, isActive: value } : offer)
        };

        setSimOffersCopy({ ...simOffersCopy, ...newOffersState });
    };
    // Get modified offers
    const getModifiedOffers = () => {
        // Flatten each list of offers into one array
        const originalOffersList = Object.values(organizationSimOffers).flat();
        const updatedOffersList = Object.values(simOffersCopy).flat();

        return updatedOffersList.filter((offer, index) => offer.isActive !== originalOffersList[index].isActive);
    };

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-3xl'>
                <HeaderTitle title='modal.update_sim_offers.title' namespace={nsOrganization} />
                <ModalBody>
                    {isLoading && <Spinner className='mt-4' />}
                    {!isLoading &&
                        <>
                            {hasData ?
                                <>
                                    {categories.length > 1 &&
                                        <div className='flex gap-4 items-center'>
                                            <Entity
                                                className='font-bold'
                                                entity='modal.update_sim_offers.subtitle'
                                                namespace={nsOrganization}
                                            />
                                            <Switch
                                                name='manage_sim_offers'
                                                checked={showAllOffers}
                                                onCheckedChange={handleSwitchChange}
                                                entity='modal.update_sim_offers.show_all'
                                                namespace={nsOrganization}
                                            />
                                        </div>}
                                    <ScrollArea className='h-96 w-full mt-4 border border-gray-300 rounded'>
                                        <Accordion
                                            type='multiple'
                                            value={accordionsOpened}
                                            onValueChange={handleAccordionChange}
                                        >
                                            {categories.map((category, indx) => (
                                                <AccordionItem value={category.name} key={category.key} index={indx}>
                                                    <AccordionTrigger>
                                                        <Entity
                                                            entity={category.entities.name}
                                                            namespace={nsOrganization}
                                                            className='font-bold text-base'
                                                        />
                                                    </AccordionTrigger>
                                                    <AccordionContent>
                                                        <div className='grid grid-cols-3'>
                                                            {simOffersCopy?.[category.name]?.map((simOffer, index) => (
                                                                <div key={simOffer.uuid} className='p-4 flex gap-2'>
                                                                    <Switch
                                                                        name='set_default_permission'
                                                                        checked={simOffer.isActive}
                                                                        onCheckedChange={(value) => handleOfferChange(value, category.name, index)}
                                                                        entity={simOffer.displayName}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </AccordionContent>
                                                </AccordionItem>
                                            ))}
                                        </Accordion>
                                    </ScrollArea>
                                </>
                                : <div className='text-center py-4'>
                                    <Entity entity='modal.update_sim_offers.no_data' namespace={nsOrganization} />
                                </div>
                            }
                        </>
                    }
                </ModalBody>
                <FooterForm
                    onSubmit={
                        () => {
                            updateSimOffers.mutate({
                                targetOrganizationUuid: organizationId,
                                offers: getModifiedOffers()
                            });
                        }
                    }
                    showSubmitButton={hasData}
                    isSubmitting={updateSimOffers.isLoading}
                    isSubmitDisabled={JSON.stringify(organizationSimOffers) === JSON.stringify(simOffersCopy)}
                />
            </ModalContent>
        </Modal>
    );
}

ModalUpdateOrganizationSimOffers.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    organizationId: PropTypes.string,
    organizationSimOffers: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
    setLaunchRefetch: PropTypes.func
};

export default ModalUpdateOrganizationSimOffers;